/**
 * useMapTransform — pan/zoom hook for the SVG adjacency map canvas.
 *
 * Uses d3-zoom for wheel zoom, drag pan, and pinch zoom (touch).
 * Exposes a reactive transform state for React to apply to a <g> wrapper.
 *
 * Usage:
 *   const { transformRef, transform, zoomIn, zoomOut, resetZoom, fitToContent } = useMapTransform();
 *   <svg ref={transformRef}>
 *     <g transform={`translate(${transform.x},${transform.y}) scale(${transform.k})`}>
 *       ... map content rendered at (0,0)-centered positions ...
 *     </g>
 *   </svg>
 */

import { useRef, useState, useCallback } from 'react';
import { zoom, select, zoomIdentity, pointer } from 'd3';
import type { ZoomBehavior } from 'd3';
import type { Position } from '../utils/positioning';

// ── Types ────────────────────────────────────────────────────────────

export interface MapTransform {
  x: number;
  y: number;
  k: number;
}

// ── Constants ────────────────────────────────────────────────────────

const MIN_ZOOM = 0.05;
const MAX_ZOOM = 5.0;
const TRANSITION_MS = 300;
const SCALE_STEP = 1.5;

// ── Zoom filter ──────────────────────────────────────────────────────

/**
 * Which events d3-zoom may handle. Shift-drag is reserved for node
 * repositioning; Ctrl/Meta clicks are reserved for linking; right-click never
 * pans. WQ-343: Ctrl/Meta are allowed on `wheel` only (d3's own default rule) —
 * a macOS trackpad pinch arrives as wheel + ctrlKey and must zoom the map, not
 * the page.
 */
export function mapZoomFilter(event: Event): boolean {
  const e = event as MouseEvent;
  if (e.shiftKey && (e.type === 'mousedown' || e.type === 'mousemove')) return false;
  if (e.type === 'wheel') return true;
  return !e.ctrlKey && !e.metaKey && !('button' in e && e.button !== 0);
}

// ── Hook ─────────────────────────────────────────────────────────────

export function useMapTransform() {
  const svgRef = useRef<SVGSVGElement | null>(null);
  const zoomRef = useRef<ZoomBehavior<SVGSVGElement, unknown> | null>(null);
  const [transform, setTransform] = useState<MapTransform>({ x: 0, y: 0, k: 1 });

  /**
   * Callback ref — attach to the <svg> element.
   * Initializes d3-zoom and sets the initial centered transform.
   */
  const transformRef = useCallback((svg: SVGSVGElement | null) => {
    // Cleanup previous instance
    if (svgRef.current) {
      select(svgRef.current).on('.zoom', null);
    }

    svgRef.current = svg;

    if (!svg) {
      zoomRef.current = null;
      return;
    }

    const zoomBehavior = zoom<SVGSVGElement, unknown>()
      .scaleExtent([MIN_ZOOM, MAX_ZOOM])
      .filter(mapZoomFilter)
      .on('zoom', (event) => {
        const t = event.transform;
        setTransform({ x: t.x, y: t.y, k: t.k });
      });

    zoomRef.current = zoomBehavior;

    const selection = select(svg);
    selection.call(zoomBehavior);

    // Initial transform: center the coordinate origin in the viewport
    const cx = svg.clientWidth / 2;
    const cy = svg.clientHeight / 2;
    zoomBehavior.transform(selection, zoomIdentity.translate(cx, cy));

    // Override double-click: zoom in by 1.5x centered on click point
    selection.on('dblclick.zoom', (event: MouseEvent) => {
      event.preventDefault();
      // WQ-339: only zoom when double-clicking empty background — double-clicking a
      // node or connection opens its editor and must not also zoom.
      const target = event.target as Element | null;
      if (target && target !== svg && !target.classList?.contains('map-bg-hit')) return;
      const z = zoomRef.current;
      if (!z) return;
      const [mx, my] = pointer(event, svg);
      const t = selection.transition().duration(TRANSITION_MS);
      z.scaleBy(t, SCALE_STEP, [mx, my]);
    });
  }, []);

  /** Zoom in by 1.5x centered on the viewport center. */
  const zoomIn = useCallback(() => {
    const svg = svgRef.current;
    const z = zoomRef.current;
    if (!svg || !z) return;
    const t = select(svg).transition().duration(TRANSITION_MS);
    z.scaleBy(t, SCALE_STEP);
  }, []);

  /** Zoom out by 1/1.5x centered on the viewport center. */
  const zoomOut = useCallback(() => {
    const svg = svgRef.current;
    const z = zoomRef.current;
    if (!svg || !z) return;
    const t = select(svg).transition().duration(TRANSITION_MS);
    z.scaleBy(t, 1 / SCALE_STEP);
  }, []);

  /** Reset to initial view (scale 1x, content centered). */
  const resetZoom = useCallback(() => {
    const svg = svgRef.current;
    const z = zoomRef.current;
    if (!svg || !z) return;
    const cx = svg.clientWidth / 2;
    const cy = svg.clientHeight / 2;
    const t = select(svg).transition().duration(TRANSITION_MS);
    z.transform(t, zoomIdentity.translate(cx, cy));
  }, []);

  /**
   * Fit all node positions into the viewport with padding.
   * Positions should be relative to canvas center (0, 0) — as returned
   * by computeAllPositions().
   * Set animate=false for instant positioning (e.g. initial load).
   */
  const fitToContent = useCallback((positions: Map<string, Position>, padding = 40, animate = true) => {
    const svg = svgRef.current;
    const z = zoomRef.current;
    if (!svg || !z || positions.size === 0) return;

    const svgWidth = svg.clientWidth;
    const svgHeight = svg.clientHeight;
    if (svgWidth === 0 || svgHeight === 0) return;

    // Compute bounding box of all node positions (centered at 0, 0)
    let minX = Infinity;
    let minY = Infinity;
    let maxX = -Infinity;
    let maxY = -Infinity;
    for (const pos of positions.values()) {
      if (pos.x < minX) minX = pos.x;
      if (pos.y < minY) minY = pos.y;
      if (pos.x > maxX) maxX = pos.x;
      if (pos.y > maxY) maxY = pos.y;
    }

    const contentWidth = maxX - minX;
    const contentHeight = maxY - minY;

    // Edge case: all nodes at same point — just center
    if (contentWidth === 0 && contentHeight === 0) {
      const tx = svgWidth / 2 - minX;
      const ty = svgHeight / 2 - minY;
      const target = zoomIdentity.translate(tx, ty);
      if (animate) {
        z.transform(select(svg).transition().duration(TRANSITION_MS), target);
      } else {
        z.transform(select(svg), target);
      }
      return;
    }

    // Scale to fit content + padding within viewport
    const k = Math.max(
      MIN_ZOOM,
      Math.min(
        MAX_ZOOM,
        (svgWidth - padding * 2) / (contentWidth || 1),
        (svgHeight - padding * 2) / (contentHeight || 1),
      ),
    );

    // Translate so the content centroid maps to the viewport center
    const contentCenterX = (minX + maxX) / 2;
    const contentCenterY = (minY + maxY) / 2;
    const tx = svgWidth / 2 - k * contentCenterX;
    const ty = svgHeight / 2 - k * contentCenterY;

    const target = zoomIdentity.translate(tx, ty).scale(k);
    if (animate) {
      z.transform(select(svg).transition().duration(TRANSITION_MS), target);
    } else {
      z.transform(select(svg), target);
    }
  }, []);

  return { transformRef, transform, zoomIn, zoomOut, resetZoom, fitToContent };
}
